import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { Loader2, Sparkles, Tag, X, RefreshCw, EyeOff, CheckCheck, Zap, Info, Settings2, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * iter477 — Inline Code Inbox shown above the Transactions tab on the
 * Bank Statements detail view. Lists ALL ALL-CAPS code-like tokens
 * found in this statement's transaction descriptions that the decoder
 * doesn't yet recognise. Each token can be:
 *   - AI-suggested (one click → AI label fills the input)
 *   - Manually labelled
 *   - Tagged as POS-settlement or Refund/Chargeback inline
 * Saving persists to the tenant Bank Code Dictionary and propagates
 * to every future statement automatically.
 *
 * Props:
 *   statementId       — Mongo id of the statement we're viewing
 *   bankAccountId     — bank_account_id for bank-scoped overrides
 *   onChanged()       — fired after a successful save so the parent
 *                       can refetch the statement & re-decode rows.
 */
export default function StatementCodeInbox({ statementId, bankAccountId, onChanged }) {
  const [loading, setLoading] = useState(true);
  const [tokens, setTokens] = useState([]); // [{token, count, sample, _label, _is_pos, _is_refund, _busy}]
  const [aiBusy, setAiBusy] = useState(false);
  const [saveAllBusy, setSaveAllBusy] = useState(false);
  // iter484 — "Your saved labels" panel state
  const [savedOpen, setSavedOpen] = useState(false);
  const [savedLabels, setSavedLabels] = useState([]); // [{code, label, source}]
  const [savedLoading, setSavedLoading] = useState(false);
  const [editing, setEditing] = useState({}); // {code: draftLabel}

  // Fetch the user's existing tenant + bank-scoped overrides so they
  // can be reviewed / edited / deleted without leaving the inbox.
  const loadSaved = useCallback(async () => {
    setSavedLoading(true);
    try {
      const r = await api.get('/banking/code-dictionary', {
        params: bankAccountId ? { bank_account_id: bankAccountId } : {},
      });
      const entries = (r.data?.entries || []).filter(e => e.source === 'custom');
      setSavedLabels(entries);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load saved labels');
    } finally {
      setSavedLoading(false);
    }
  }, [bankAccountId]);

  const deleteSaved = async (code) => {
    if (!window.confirm(`Delete the label for "${code}"? Future statements will fall back to the default decode (or no decode).`)) return;
    try {
      await api.delete(`/banking/code-dictionary/${encodeURIComponent(code)}`);
      toast.success(`Removed "${code}".`);
      setSavedLabels(ls => ls.filter(l => l.code !== code));
      onChanged?.();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const saveEdit = async (code) => {
    const newLabel = (editing[code] || '').trim();
    if (!newLabel) { toast.error('Label cannot be empty'); return; }
    try {
      await api.post('/banking/code-dictionary/add', { code, label: newLabel });
      toast.success(`Updated "${code}" → ${newLabel}`);
      setSavedLabels(ls => ls.map(l => l.code === code ? { ...l, label: newLabel } : l));
      setEditing(e => { const { [code]: _, ...rest } = e; return rest; });
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const clearAllCustom = async () => {
    if (!savedLabels.length) return;
    if (!window.confirm(`Clear ALL ${savedLabels.length} of your custom labels? Built-in defaults stay. This cannot be undone.`)) return;
    try {
      await api.post('/banking/code-dictionary', { entries: {} });
      toast.success(`Cleared ${savedLabels.length} custom labels.`);
      setSavedLabels([]);
      onChanged?.();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Clear-all failed');
    }
  };

  const load = useCallback(async () => {
    if (!statementId) return;
    setLoading(true);
    try {
      const r = await api.get('/banking/code-dictionary/trending-unknown', {
        params: { statement_id: statementId, days: 1095, limit: 200 },
      });
      const out = (r.data?.tokens || []).map(t => ({
        ...t,
        _label: '',
        _is_pos: false,
        _is_refund: false,
        _busy: false,
      }));
      setTokens(out);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load unknown codes');
    } finally {
      setLoading(false);
    }
  }, [statementId]);

  useEffect(() => { load(); }, [load]);

  const updateOne = (idx, patch) => setTokens(ts => ts.map((t, i) => i === idx ? { ...t, ...patch } : t));

  // One-click AI label for a single token.
  const aiSuggestOne = async (idx) => {
    const t = tokens[idx];
    updateOne(idx, { _busy: true });
    try {
      const r = await api.post('/banking/code-dictionary/ai-suggest-label', {
        token: t.token,
        sample: (t.samples && t.samples[0]) || t.sample || '',
        // iter480 — also forward the multi-samples + co-tokens so the
        // LLM sees the same context the user sees in the inbox.
        samples: t.samples || (t.sample ? [t.sample] : []),
        co_tokens: t.co_tokens || [],
      });
      const label = r.data?.label || '';
      const cat = (r.data?.category_hint || '').toLowerCase();
      updateOne(idx, {
        _label: label,
        _is_pos: cat.includes('pos') || cat.includes('settle'),
        _is_refund: cat.includes('refund') || cat.includes('reversal'),
        _ai_used: !!label,
        _ai_model: r.data?.model || 'claude-haiku-4-5',
        _busy: false,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI suggest failed');
      updateOne(idx, { _busy: false });
    }
  };

  // Run AI on every unlabelled token in this statement.
  // Backend caps each batch at 10, so we chunk client-side and stitch
  // the results back together. iter481 — also corrected the response
  // field (backend returns `results`, not `items`) and started reading
  // the per-row `suggest_pos_settlement` / `suggest_refund` flags that
  // the batch endpoint already emits.
  const aiSuggestAll = async () => {
    setAiBusy(true);
    try {
      const todo = tokens.filter(t => !t._label);
      if (!todo.length) {
        toast.info('Nothing to suggest — all tokens already have a draft label.');
        return;
      }
      const labelMap = {};
      const CHUNK = 10;
      for (let i = 0; i < todo.length; i += CHUNK) {
        const slice = todo.slice(i, i + CHUNK);
        const payload = slice.map(t => ({
          token: t.token,
          sample: (t.samples && t.samples[0]) || t.sample || '',
          samples: t.samples || (t.sample ? [t.sample] : []),
          co_tokens: t.co_tokens || [],
        }));
        const r = await api.post('/banking/code-dictionary/ai-suggest-labels-batch', { items: payload });
        for (const it of (r.data?.results || [])) {
          labelMap[it.token] = {
            label: it.label || '',
            is_pos: !!it.suggest_pos_settlement,
            is_refund: !!it.suggest_refund,
          };
        }
      }
      let filled = 0;
      setTokens(ts => ts.map(t => {
        if (!t._label && labelMap[t.token] && labelMap[t.token].label) {
          filled += 1;
          const c = labelMap[t.token];
          return {
            ...t,
            _label: c.label,
            _is_pos: c.is_pos,
            _is_refund: c.is_refund,
            _ai_used: true,
            _ai_model: 'claude-haiku-4-5',
          };
        }
        return t;
      }));
      if (filled === 0) {
        toast.warning('AI returned no usable labels. You can type them manually.');
      } else {
        toast.success(`AI filled ${filled} draft label${filled === 1 ? '' : 's'}. Review and click "Save all" to commit.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI batch suggest failed');
    } finally {
      setAiBusy(false);
    }
  };

  // iter481 — Persist every token that has a non-empty draft label in
  // one call. Saved rows drop out of the inbox immediately, failures
  // stay so the user can retry or edit.
  const saveAllSuggestions = async () => {
    const ready = tokens.filter(t => t._label && t._label.trim());
    if (!ready.length) {
      toast.info('Nothing to save — fill at least one label first (try "AI for all").');
      return;
    }
    setSaveAllBusy(true);
    try {
      const items = ready.map(t => ({
        code: t.token,
        label: t._label.trim(),
        is_pos: !!t._is_pos,
        is_refund: !!t._is_refund,
        bank_account_id: bankAccountId || undefined,
      }));
      const r = await api.post('/banking/code-dictionary/add-batch', { items });
      const saved = r.data?.saved || 0;
      const failed = r.data?.failed || [];
      const savedCodes = new Set((r.data?.items || []).map(i => i.code));
      setTokens(ts => ts.filter(t => !savedCodes.has(t.token)));
      if (saved > 0) {
        toast.success(`Saved ${saved} code${saved === 1 ? '' : 's'}. Applied to every statement.`);
      }
      if (failed.length) {
        toast.error(`${failed.length} failed to save. Edit and retry.`);
      }
      if (saved > 0) onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Batch save failed');
    } finally {
      setSaveAllBusy(false);
    }
  };

  const saveOne = async (idx) => {
    const t = tokens[idx];
    if (!t._label.trim()) { toast.error('Type or AI-suggest a label first'); return; }
    updateOne(idx, { _busy: true });
    try {
      // 1) Save the label (tenant- or bank-scoped)
      await api.post('/banking/code-dictionary/add', {
        code: t.token,
        label: t._label.trim(),
        bank_account_id: bankAccountId || undefined,
      });
      // 2) Set flags (separate endpoints — keeps the dictionary core simple)
      if (t._is_pos) {
        await api.put(`/banking/code-dictionary/pos-settlement/${encodeURIComponent(t.token)}`, { is_pos_settlement: true });
      }
      if (t._is_refund) {
        await api.put(`/banking/code-dictionary/refund/${encodeURIComponent(t.token)}`, { is_refund: true });
      }
      toast.success(`Saved "${t.token}" → ${t._label}. Applied to all statements.`);
      // Remove from the inbox (it's no longer unknown)
      setTokens(ts => ts.filter((_, i) => i !== idx));
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
      updateOne(idx, { _busy: false });
    }
  };

  // iter482 — One-click power button: AI suggest + commit in a single
  // pass. Bypasses React state churn between two clicks; if anything
  // fails (AI returned blank, save errored) we surface the error and
  // leave the row untouched so the user can fall back to manual flow.
  const quickLabelAndSave = async (idx) => {
    const t = tokens[idx];
    updateOne(idx, { _busy: true });
    try {
      // 1) AI suggest
      const ai = await api.post('/banking/code-dictionary/ai-suggest-label', {
        token: t.token,
        sample: (t.samples && t.samples[0]) || t.sample || '',
        samples: t.samples || (t.sample ? [t.sample] : []),
        co_tokens: t.co_tokens || [],
      });
      const label = (ai.data?.label || '').trim();
      if (!label) {
        toast.warning(`AI couldn't suggest a label for "${t.token}". Type one manually.`);
        updateOne(idx, { _busy: false });
        return;
      }
      const cat = (ai.data?.category_hint || '').toLowerCase();
      const isPos = cat.includes('pos') || cat.includes('settle');
      const isRefund = cat.includes('refund') || cat.includes('reversal');

      // 2) Commit via batch endpoint (single round-trip, sets flags too)
      const r = await api.post('/banking/code-dictionary/add-batch', {
        items: [{
          code: t.token,
          label,
          is_pos: isPos,
          is_refund: isRefund,
          bank_account_id: bankAccountId || undefined,
        }],
      });
      const saved = r.data?.saved || 0;
      if (saved < 1) {
        const err = r.data?.failed?.[0]?.error || 'Save failed';
        throw new Error(err);
      }
      toast.success(`✨ "${t.token}" → ${label}. Applied to all statements.`);
      setTokens(ts => ts.filter((_, i) => i !== idx));
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Quick label & save failed');
      updateOne(idx, { _busy: false });
    }
  };

  // iter484 — Reusable "Manage saved labels" disclosure block, shown
  // both when there are unknown tokens AND when the inbox is clean,
  // so users can audit/edit/clear their dictionary either way.
  const savedPanel = (
    <div className="mb-3 p-2.5 bg-white border border-violet-200 rounded-xl text-xs" data-testid="stmt-code-saved-panel">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-2 text-violet-900 hover:text-violet-700"
        onClick={() => { setSavedOpen(o => !o); if (!savedOpen && !savedLabels.length) loadSaved(); }}
        data-testid="stmt-code-saved-toggle"
      >
        <span className="flex items-center gap-1.5 font-semibold">
          <Settings2 size={13} />
          Your saved labels{savedLabels.length ? ` (${savedLabels.length})` : ''}
        </span>
        <span className="text-violet-500">{savedOpen ? '▾' : '▸'}</span>
      </button>
      {savedOpen && (
        <div className="mt-2 space-y-1" data-testid="stmt-code-saved-list">
          {savedLoading && (
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Loader2 size={11} className="animate-spin" /> Loading…
            </div>
          )}
          {!savedLoading && savedLabels.length === 0 && (
            <div className="text-muted-foreground italic">
              You haven't taught the system any custom labels yet. Save your first one below.
            </div>
          )}
          {!savedLoading && savedLabels.length > 0 && (
            <>
              <div className="flex items-center justify-between gap-2 pb-1 border-b border-violet-100">
                <span className="text-[10px] text-muted-foreground">
                  Click a label to edit, X to delete. Defaults are never shown here.
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 text-[10px] px-2 text-rose-700 border-rose-300 hover:bg-rose-50"
                  onClick={clearAllCustom}
                  data-testid="stmt-code-saved-clear-all"
                >
                  <AlertTriangle size={10} className="mr-1" /> Clear all
                </Button>
              </div>
              <div className="max-h-64 overflow-y-auto space-y-0.5 pt-1">
                {savedLabels.map(e => (
                  <div key={e.code} className="flex items-center gap-1.5 py-0.5" data-testid={`stmt-code-saved-${e.code}`}>
                    <code className="text-[10px] font-mono px-1.5 py-0.5 bg-violet-50 border border-violet-200 rounded text-violet-900 shrink-0 min-w-[90px]">{e.code}</code>
                    {editing[e.code] !== undefined ? (
                      <>
                        <Input
                          className="h-6 text-[11px] flex-1 min-w-[120px]"
                          value={editing[e.code]}
                          autoFocus
                          onChange={(ev) => setEditing(s => ({ ...s, [e.code]: ev.target.value }))}
                          onKeyDown={(ev) => { if (ev.key === 'Enter') saveEdit(e.code); if (ev.key === 'Escape') setEditing(s => { const { [e.code]: _, ...rest } = s; return rest; }); }}
                          data-testid={`stmt-code-saved-edit-input-${e.code}`}
                        />
                        <Button size="sm" className="h-6 text-[10px] px-2 bg-violet-600 hover:bg-violet-700 text-white" onClick={() => saveEdit(e.code)}>
                          Save
                        </Button>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditing(s => { const { [e.code]: _, ...rest } = s; return rest; })}>
                          <X size={11} />
                        </Button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="flex-1 text-left text-[11px] text-foreground hover:text-violet-700 truncate"
                          onClick={() => setEditing(s => ({ ...s, [e.code]: e.label }))}
                          title="Click to edit"
                          data-testid={`stmt-code-saved-edit-${e.code}`}
                        >
                          {e.label}
                          {e.is_pos_settlement && <Badge variant="outline" className="ml-1 text-[9px]">POS</Badge>}
                          {e.is_refund && <Badge variant="outline" className="ml-1 text-[9px]">Refund</Badge>}
                        </button>
                        <button
                          type="button"
                          className="p-1 text-rose-600 hover:text-rose-800 shrink-0"
                          onClick={() => deleteSaved(e.code)}
                          title={`Delete "${e.code}"`}
                          data-testid={`stmt-code-saved-delete-${e.code}`}
                        >
                          <Trash2 size={11} />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 flex items-center gap-2" data-testid="stmt-code-inbox-loading">
        <Loader2 size={14} className="animate-spin" /> Scanning this statement for unknown codes…
      </div>
    );
  }

  if (tokens.length === 0) {
    return (
      <>
        {savedPanel}
        <div className="mb-3 p-2.5 bg-success/10 border border-success/30 rounded-xl text-xs text-success flex items-center gap-2" data-testid="stmt-code-inbox-clean">
          <Tag size={14} /> Every code in this statement is mapped — clean.
        </div>
      </>
    );
  }

  return (
    <>
      {savedPanel}
    <div className="mb-3 p-3 bg-amber-50/70 border border-amber-200 rounded-xl" data-testid="stmt-code-inbox">
      <div className="flex items-start justify-between gap-3 mb-2.5">
        <div>
          <div className="text-xs font-semibold text-amber-900 flex items-center gap-1.5">
            <Tag size={14} />
            {tokens.length} unknown code{tokens.length === 1 ? '' : 's'} in this statement
          </div>
          <div className="text-[11px] text-amber-800 mt-0.5">
            Label them once and every future statement row containing the same code will auto-tag with the same category, POS, and refund flags.
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button size="sm" variant="outline" className="h-7 text-xs bg-white" onClick={aiSuggestAll} disabled={aiBusy || saveAllBusy} data-testid="stmt-code-inbox-ai-all">
            {aiBusy ? <Loader2 size={13} className="animate-spin mr-1" /> : <Sparkles size={13} className="mr-1" />} AI for all
          </Button>
          {/* iter481 — Batch-commit every drafted label in one click. */}
          {tokens.some(t => t._label && t._label.trim()) && (
            <Button
              size="sm"
              className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={saveAllSuggestions}
              disabled={saveAllBusy || aiBusy}
              data-testid="stmt-code-inbox-save-all"
              title="Save every token that already has a draft label"
            >
              {saveAllBusy ? <Loader2 size={13} className="animate-spin mr-1" /> : <CheckCheck size={13} className="mr-1" />}
              Save all ({tokens.filter(t => t._label && t._label.trim()).length})
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={load} title="Refresh">
            <RefreshCw size={13} />
          </Button>
        </div>
      </div>

      <div className="space-y-1.5">
        {tokens.map((t, idx) => {
          const co = t.co_tokens || [];
          const samples = t.samples || (t.sample ? [t.sample] : []);
          return (
            <div key={t.token} className="p-1.5 bg-white border border-amber-100 rounded-md" data-testid={`stmt-code-token-${t.token}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <code className="text-[11px] font-mono px-1.5 py-0.5 bg-amber-100 text-amber-900 rounded shrink-0">{t.token}</code>
                <Badge variant="outline" className="text-[10px] shrink-0">{t.count}×</Badge>
                <Input
                  className="h-7 text-xs flex-1 min-w-[150px]"
                  placeholder={samples[0] ? `e.g. "${samples[0].slice(0, 40).trim()}…"` : 'Type a label'}
                  value={t._label}
                  onChange={(e) => updateOne(idx, { _label: e.target.value, _ai_used: false })}
                  data-testid={`stmt-code-label-${t.token}`}
                />
                {/* iter483 — "Why this label?" — shows the exact context the
                    LLM consumed so the accountant can audit the suggestion
                    before the ⚡ power button auto-saves it. */}
                {t._ai_used && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="shrink-0 p-1 text-violet-600 hover:text-violet-800"
                        title="Why this label?"
                        data-testid={`stmt-code-why-${t.token}`}
                      >
                        <Info size={13} />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-80 text-xs p-3">
                      <div className="font-semibold text-violet-900 mb-1 flex items-center gap-1.5">
                        <Sparkles size={12} /> Why "{t._label}"?
                      </div>
                      <div className="text-[10px] text-muted-foreground mb-2">
                        AI ({t._ai_model || 'claude-haiku-4-5'}) based this label on the context below. Edit the input to override.
                      </div>
                      {samples.length > 0 && (
                        <div className="mb-2">
                          <div className="text-[10px] font-semibold text-muted-foreground uppercase mb-0.5">Sample line{samples.length === 1 ? '' : 's'} used</div>
                          <ul className="space-y-0.5">
                            {samples.slice(0, 3).map((s, i) => (
                              <li key={i} className="text-[11px] font-mono bg-muted/40 rounded px-1.5 py-0.5 break-all" title={s}>
                                {s.slice(0, 140)}{s.length > 140 ? '…' : ''}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {co.length > 0 && (
                        <div>
                          <div className="text-[10px] font-semibold text-muted-foreground uppercase mb-0.5">Co-tokens seen alongside</div>
                          <div className="flex flex-wrap gap-1">
                            {co.slice(0, 8).map(c => (
                              <code key={c.token} className="text-[10px] font-mono px-1.5 py-0.5 bg-amber-50 border border-amber-200 rounded text-amber-900">
                                {c.token} <span className="opacity-60">×{c.count}</span>
                              </code>
                            ))}
                          </div>
                        </div>
                      )}
                      {samples.length === 0 && co.length === 0 && (
                        <div className="text-[11px] text-muted-foreground italic">
                          The AI worked from the token only — no extra context was available.
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                )}
                <label className="flex items-center gap-1 text-[10px] cursor-pointer shrink-0">
                  <input type="checkbox" checked={t._is_pos} onChange={(e) => updateOne(idx, { _is_pos: e.target.checked })} data-testid={`stmt-code-pos-${t.token}`} /> POS
                </label>
                <label className="flex items-center gap-1 text-[10px] cursor-pointer shrink-0">
                  <input type="checkbox" checked={t._is_refund} onChange={(e) => updateOne(idx, { _is_refund: e.target.checked })} data-testid={`stmt-code-refund-${t.token}`} /> Refund
                </label>
                <Button size="sm" variant="outline" className="h-7 w-7 p-0 bg-white" onClick={() => aiSuggestOne(idx)} disabled={t._busy} title="AI suggest just this token">
                  {t._busy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                </Button>
                {/* iter482 — Power button: AI suggest + save in one click. */}
                <Button
                  size="sm"
                  className="h-7 w-7 p-0 bg-violet-600 hover:bg-violet-700 text-white"
                  onClick={() => quickLabelAndSave(idx)}
                  disabled={t._busy}
                  title="Quick: AI label & save in one click"
                  data-testid={`stmt-code-quick-${t.token}`}
                >
                  {t._busy ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />}
                </Button>
                <Button size="sm" className="h-7 text-[11px] px-2 bg-amber-600 hover:bg-amber-700 text-white" onClick={() => saveOne(idx)} disabled={t._busy || !t._label.trim()} data-testid={`stmt-code-save-${t.token}`}>
                  Save
                </Button>
                {/* iter478 — Dismiss menu: hide here vs ignore globally */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" className="p-1 text-muted-foreground hover:text-foreground" title="Dismiss options" data-testid={`stmt-code-dismiss-menu-${t.token}`}>
                      <X size={13} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64 text-xs">
                    <DropdownMenuItem onClick={() => setTokens(ts => ts.filter((_, i) => i !== idx))} data-testid={`stmt-code-dismiss-here-${t.token}`}>
                      <X size={13} className="mr-1.5" />
                      Dismiss for this statement only
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-rose-700 focus:text-rose-800 focus:bg-rose-50"
                      onClick={async () => {
                        if (!window.confirm(`Permanently ignore "${t.token}"? It won't appear in the inbox for any future statement on this bank.`)) return;
                        updateOne(idx, { _busy: true });
                        try {
                          await api.post('/banking/code-dictionary/ignore', { token: t.token, bank_account_id: bankAccountId || undefined, reason: 'Marked as bank-internal noise by user' });
                          toast.success(`"${t.token}" added to ignore-list. Won't appear again.`);
                          setTokens(ts => ts.filter((_, i) => i !== idx));
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Ignore failed');
                          updateOne(idx, { _busy: false });
                        }
                      }}
                      data-testid={`stmt-code-ignore-forever-${t.token}`}
                    >
                      <EyeOff size={13} className="mr-1.5" />
                      Add to ignore-list (permanent)
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* iter479 — Context row: shows co-occurring tokens (clickable
                  to drop into the label input) + the second/third sample so
                  the user can recognise WHO this stamp belongs to (e.g.
                  "D0001-C0000" → also sees ABC TRADING in the same line). */}
              {(co.length > 0 || samples.length > 1) && (
                <div className="pl-1.5 pt-1 text-[10px] text-amber-800 space-y-0.5" data-testid={`stmt-code-context-${t.token}`}>
                  {co.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="text-amber-700">Often seen with:</span>
                      {co.map(c => (
                        <button
                          type="button"
                          key={c.token}
                          onClick={() => updateOne(idx, { _label: t._label ? `${t._label} ${c.token}` : c.token })}
                          className="font-mono px-1.5 py-0.5 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded text-amber-900"
                          title={`Add "${c.token}" to the label`}
                          data-testid={`stmt-code-cotoken-${t.token}-${c.token}`}
                        >
                          {c.token} <span className="opacity-60">×{c.count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {samples.slice(1).map((s, i) => (
                    <div key={i} className="text-amber-700/80 truncate" title={s}>
                      • {s.slice(0, 110)}{s.length > 110 ? '…' : ''}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {tokens[0]?.sample && (
        <div className="mt-2 text-[10px] text-amber-700">
          Tip: <Zap size={9} className="inline" /> = AI label & save in one click · <Sparkles size={9} className="inline" /> = AI suggest only · <strong>AI for all</strong> drafts every label, then <strong>Save all</strong> commits.
        </div>
      )}
    </div>
    </>
  );
}
